import React, { ReactNode, useCallback, useEffect, useMemo } from "react"
import { Tabs, useThemeColors } from "../../native-ui";
import TabItem from "../../native-ui/Tabs/TabItem";
import useSafeCallback from "../../hooks/useSafeCallback";
import useLatest from "~/hooks/useLatest";

type TabProps = {
    title: string;
    icon?: string;
    iconFamily?: string;
    key: string | number;
    children: ReactNode;
    description?: string;
    disabled?: boolean;
    variant?: "wizard" | "tabs";
}

type ConnectedTabsRowProps = {
    tabs: TabProps[];
    defaultValue?: string | number;
    modelId: string;
    view: boolean;

    lastSavedAt?: number | null;
    variant?: TabProps['variant'];
    name?: string
    referenceField?: string;
    value?: string | number;
    onChange?: (tab: string | number) => void;
    controlled?: boolean;

    errorTabs?: string[];
}



const TabsRow = React.memo((props: ConnectedTabsRowProps) => {
    const [value, onChange] = React.useState(props.value ?? props.defaultValue ?? props.tabs[0].key);

    const resolvedCurrentTab = (props.value ?? value) as string;

    const currentTab = React.useMemo(() => props.tabs.find((tab => tab.key === resolvedCurrentTab)) ?? props.tabs[0], [props.tabs, resolvedCurrentTab]);
    const renderFunc = useSafeCallback(() => {
        return <>{ currentTab?.children }</>
    });

    let firstErrorTab = props.errorTabs[0];
    const latestTab = useLatest(resolvedCurrentTab);


    useEffect(() => {
        if (!firstErrorTab) return;
        if (latestTab.current === firstErrorTab) return;
        if (!props.controlled) onChange(firstErrorTab);
        else props.onChange?.(firstErrorTab);
    }, [props.controlled, firstErrorTab, props.lastSavedAt]);


    const handleChange = useCallback((nextTab: string) => {
        if (!props.controlled) onChange(nextTab);
        else props.onChange?.(nextTab);
    }, [props.onChange]);

    const {primaryColor, errorColor } = useThemeColors();

    const activeColor = useMemo(() => props.errorTabs?.includes(resolvedCurrentTab as string) ? errorColor : primaryColor, [props.errorTabs, resolvedCurrentTab]);


    return (
        <Tabs value={currentTab.key as string} onChange={handleChange} activeColor={activeColor} scrollable>
            {
                props.tabs.map((tab, i) => <TabItem
                    key={tab.key ?? i}
                    name={tab.key as string}
                    iconName={tab.icon}
                    iconType={tab.iconFamily}
                    label={tab.title}
                    disabled={tab.disabled}
                    isInvalid={props.errorTabs.includes(tab.key as string)}
                    render={renderFunc}
                    mode={props.variant}
                    index={i}
                />)
            }
        </Tabs>
    )
});

export default TabsRow;